import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Plus, Minus, ShoppingBag, ArrowRight, ArrowLeft } from 'lucide-react';
import Header from './components/Header21';
import Footer from './components/Footer21';

const Cart21 = () => {
    const navigate = useNavigate();
    const [showEmpty, setShowEmpty] = useState(false);

    const [cartItems, setCartItems] = useState([
        {
            id: 1,
            name: 'Midnight Zari Lehenga',
            price: 48500,
            quantity: 1,
            size: 'M',
            color: 'Deep Maroon',
            image: 'https://images.pexels.com/photos/9419032/pexels-photo-9419032.jpeg?auto=compress&cs=tinysrgb&w=800',
        },
        {
            id: 2,
            name: 'Noir Silk Saree',
            price: 32000,
            quantity: 1,
            size: 'Free Size',
            color: 'Onyx Black',
            image: 'https://images.pexels.com/photos/11707056/pexels-photo-11707056.jpeg?auto=compress&cs=tinysrgb&w=800',
        },
        {
            id: 3,
            name: 'Embroidered Anarkali Suite',
            price: 27500,
            quantity: 2,
            size: 'S',
            color: 'Ivory Gold',
            image: 'https://images.pexels.com/photos/6687182/pexels-photo-6687182.jpeg?auto=compress&cs=tinysrgb&w=800',
        },
    ]);

    const updateQuantity = (id, delta) => {
        setCartItems(items =>
            items.map(item =>
                item.id === id
                    ? { ...item, quantity: Math.max(1, item.quantity + delta) }
                    : item
            )
        );
    };

    const removeItem = (id) => {
        setCartItems(items => items.filter(item => item.id !== id));
    };

    const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const shipping = subtotal > 25000 ? 0 : 500;
    const total = subtotal + shipping;

    const isEmpty = showEmpty || cartItems.length === 0;

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:wght@300;400;500;600;700&display=swap');
            `}</style>

            <div style={{ fontFamily: "'DM Sans', -apple-system, sans-serif" }} className="min-h-screen bg-[#0A0A0A]">
                <Header cartCount={cartItems.length} />

                <main className="pt-24 pb-16 px-4 lg:px-8">
                    <div className="max-w-[1400px] mx-auto">
                        {/* Page Header */}
                        <div className="mb-10">
                            <p className="text-[#C9A96E] text-sm tracking-[0.3em] uppercase mb-3">Shopping</p>
                            <h1
                                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                className="text-3xl lg:text-5xl font-light text-[#C9A96E] tracking-wide"
                            >
                                Your Bag
                            </h1>
                        </div>

                        {isEmpty ? (
                            /* Empty State */
                            <div className="flex flex-col items-center justify-center py-24">
                                <div className="w-24 h-24 border border-[#2A2A2A] rounded-full flex items-center justify-center mb-8">
                                    <ShoppingBag className="w-10 h-10 text-[#8A8A7A]" />
                                </div>
                                <h2
                                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                    className="text-2xl lg:text-3xl font-light text-[#F5F0EB] mb-3"
                                >
                                    Your bag is empty
                                </h2>
                                <p className="text-[#8A8A7A] text-sm mb-8 max-w-sm text-center">
                                    Discover our curated collections and find pieces that speak to your soul.
                                </p>
                                <button
                                    onClick={() => navigate('/template21/shop')}
                                    className="inline-flex items-center gap-2 px-8 py-3 border border-[#C9A96E] text-[#C9A96E] text-sm font-medium tracking-wider uppercase hover:bg-[#C9A96E] hover:text-[#0A0A0A] transition-all duration-300"
                                >
                                    Continue Shopping
                                    <ArrowRight size={16} />
                                </button>
                            </div>
                        ) : (
                            /* Cart Content */
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
                                {/* Cart Items */}
                                <div className="lg:col-span-2 space-y-0">
                                    {/* Column Headers (Desktop) */}
                                    <div className="hidden md:grid grid-cols-12 gap-4 pb-4 border-b border-[#2A2A2A] mb-0">
                                        <span className="col-span-6 text-[#8A8A7A] text-xs tracking-wider uppercase">Product</span>
                                        <span className="col-span-2 text-[#8A8A7A] text-xs tracking-wider uppercase text-center">Quantity</span>
                                        <span className="col-span-2 text-[#8A8A7A] text-xs tracking-wider uppercase text-right">Price</span>
                                        <span className="col-span-2 text-[#8A8A7A] text-xs tracking-wider uppercase text-right">Total</span>
                                    </div>

                                    {cartItems.map((item) => (
                                        <div
                                            key={item.id}
                                            className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center py-6 border-b border-[#2A2A2A] bg-[#141414] md:bg-transparent px-4 md:px-0 rounded-lg md:rounded-none mb-3 md:mb-0"
                                        >
                                            {/* Product Info */}
                                            <div className="md:col-span-6 flex gap-4 items-center">
                                                <div className="w-20 h-[100px] flex-shrink-0 overflow-hidden rounded">
                                                    <img
                                                        src={item.image}
                                                        alt={item.name}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="text-[#F5F0EB] text-sm font-medium mb-1 truncate">{item.name}</h3>
                                                    <p className="text-[#8A8A7A] text-xs mb-0.5">Size: {item.size}</p>
                                                    <p className="text-[#8A8A7A] text-xs">Color: {item.color}</p>
                                                    <p className="text-[#C9A96E] text-sm font-medium mt-1 md:hidden">
                                                        {'\u20B9'}{item.price.toLocaleString('en-IN')}
                                                    </p>
                                                </div>
                                                {/* Remove - Mobile */}
                                                <button
                                                    onClick={() => removeItem(item.id)}
                                                    className="md:hidden text-[#8A8A7A] hover:text-[#C9A96E] transition-colors p-1"
                                                    aria-label="Remove item"
                                                >
                                                    <X size={18} />
                                                </button>
                                            </div>

                                            {/* Quantity */}
                                            <div className="md:col-span-2 flex items-center justify-center">
                                                <div className="flex items-center border border-[#2A2A2A] rounded">
                                                    <button
                                                        onClick={() => updateQuantity(item.id, -1)}
                                                        className="w-9 h-9 flex items-center justify-center text-[#C9A96E] hover:bg-[#1A1A1A] transition-colors"
                                                        aria-label="Decrease quantity"
                                                    >
                                                        <Minus size={14} />
                                                    </button>
                                                    <span className="w-10 text-center text-[#F5F0EB] text-sm font-medium">
                                                        {item.quantity}
                                                    </span>
                                                    <button
                                                        onClick={() => updateQuantity(item.id, 1)}
                                                        className="w-9 h-9 flex items-center justify-center text-[#C9A96E] hover:bg-[#1A1A1A] transition-colors"
                                                        aria-label="Increase quantity"
                                                    >
                                                        <Plus size={14} />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Unit Price (Desktop) */}
                                            <div className="hidden md:flex md:col-span-2 justify-end">
                                                <span className="text-[#C9A96E] text-sm font-medium">
                                                    {'\u20B9'}{item.price.toLocaleString('en-IN')}
                                                </span>
                                            </div>

                                            {/* Line Total + Remove (Desktop) */}
                                            <div className="hidden md:flex md:col-span-2 items-center justify-end gap-3">
                                                <span className="text-[#F5F0EB] text-sm font-semibold">
                                                    {'\u20B9'}{(item.price * item.quantity).toLocaleString('en-IN')}
                                                </span>
                                                <button
                                                    onClick={() => removeItem(item.id)}
                                                    className="text-[#8A8A7A] hover:text-[#C9A96E] transition-colors p-1"
                                                    aria-label="Remove item"
                                                >
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Cart Summary Sidebar */}
                                <div className="lg:col-span-1">
                                    <div className="bg-[#141414] border border-[#2A2A2A] rounded-lg p-6 lg:sticky lg:top-24">
                                        <h2
                                            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                            className="text-xl font-medium text-[#F5F0EB] mb-6 tracking-wide"
                                        >
                                            Order Summary
                                        </h2>

                                        {/* Price Breakdown */}
                                        <div className="space-y-4 mb-6 pb-6 border-b border-[#2A2A2A]">
                                            <div className="flex justify-between">
                                                <span className="text-[#8A8A7A] text-sm">
                                                    Subtotal ({cartItems.reduce((s, i) => s + i.quantity, 0)} items)
                                                </span>
                                                <span className="text-[#F5F0EB] text-sm">
                                                    {'\u20B9'}{subtotal.toLocaleString('en-IN')}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-[#8A8A7A] text-sm">Shipping</span>
                                                <span className="text-[#F5F0EB] text-sm">
                                                    {shipping === 0 ? 'Complimentary' : `\u20B9${shipping}`}
                                                </span>
                                            </div>
                                            {shipping === 0 && (
                                                <p className="text-[#2ECC71] text-xs italic">
                                                    Complimentary shipping on orders above {'\u20B9'}25,000
                                                </p>
                                            )}
                                        </div>

                                        {/* Total */}
                                        <div className="flex justify-between items-center mb-8">
                                            <span
                                                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                                className="text-lg font-medium text-[#F5F0EB]"
                                            >
                                                Total
                                            </span>
                                            <span
                                                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                                className="text-2xl font-semibold text-[#C9A96E]"
                                            >
                                                {'\u20B9'}{total.toLocaleString('en-IN')}
                                            </span>
                                        </div>

                                        {/* Checkout Button */}
                                        <button
                                            onClick={() => navigate('/template21/checkout')}
                                            className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#C9A96E] text-[#0A0A0A] text-sm font-semibold tracking-wider uppercase hover:bg-[#D4B87A] transition-colors duration-300"
                                        >
                                            Proceed to Checkout
                                            <ArrowRight size={16} />
                                        </button>

                                        {/* Continue Shopping */}
                                        <button
                                            onClick={() => navigate('/template21/shop')}
                                            className="w-full flex items-center justify-center gap-2 mt-3 px-6 py-3 border border-[#2A2A2A] text-[#8A8A7A] text-sm tracking-wider uppercase hover:border-[#C9A96E] hover:text-[#C9A96E] transition-all duration-300"
                                        >
                                            <ArrowLeft size={14} />
                                            Continue Shopping
                                        </button>

                                        {/* Trust Indicators */}
                                        <div className="mt-6 pt-6 border-t border-[#2A2A2A] space-y-3">
                                            <div className="flex items-center gap-2">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C9A96E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                                </svg>
                                                <span className="text-[#8A8A7A] text-xs">Secure SSL Checkout</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C9A96E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                                </svg>
                                                <span className="text-[#8A8A7A] text-xs">Authenticity Guaranteed</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C9A96E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="20 12 20 22 4 22 4 12" />
                                                    <rect x="2" y="7" width="20" height="5" />
                                                    <line x1="12" y1="22" x2="12" y2="7" />
                                                    <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
                                                    <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
                                                </svg>
                                                <span className="text-[#8A8A7A] text-xs">Luxury Gift Packaging</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </main>

                <Footer />
            </div>
        </>
    );
};

export default Cart21;
